import React, { useEffect, useState } from "react";
import { getEmployees, getAdminMetersList } from "../services/api";
import { format, startOfMonth, endOfMonth, subMonths, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { cloudinaryThumb } from "../services/cloudinary";

export default function StatsPage() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    getEmployees()
      .then((data) => { setEmployees(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function loadStats(emp, p = period) {
    setSelected(emp);
    setLoadingStats(true);
    setStats(null);
    try {
      const now = new Date();
      const from15 = format(subDays(now, 29), "yyyy-MM-dd");
      const to15 = format(now, "yyyy-MM-dd");

      const fromPeriod = p === "month" ? format(startOfMonth(now), "yyyy-MM-dd") : undefined;
      const toPeriod = p === "month" ? format(endOfMonth(now), "yyyy-MM-dd") : undefined;
      const fromPrev = p === "month" ? format(startOfMonth(subMonths(now, 1)), "yyyy-MM-dd") : undefined;
      const toPrev = p === "month" ? format(endOfMonth(subMonths(now, 1)), "yyyy-MM-dd") : undefined;

      // Usar getAdminMetersList filtrando solo medidores chequeados por el auditor
      const [result15, resultPeriod, resultPrev] = await Promise.all([
        getAdminMetersList({ userId: emp.id, status: "checked", from: from15, to: to15, limit: 500 }),
        getAdminMetersList({ userId: emp.id, status: "checked", from: fromPeriod, to: toPeriod, limit: 1 }),
        p === "month"
          ? getAdminMetersList({ userId: emp.id, status: "checked", from: fromPrev, to: toPrev, limit: 1 })
          : Promise.resolve({ pagination: { total: 0 } }),
      ]);

      const readings = result15.data;
      const total = resultPeriod.pagination?.total || 0;
      const totalPrev = resultPrev.pagination?.total || 0;

      // Generar los últimos 30 días y agrupar por checked_at (fecha real de medición)
      const last15 = Array.from({ length: 30 }, (_, i) => {
        const d = subDays(now, 29 - i);
        return format(d, "yyyy-MM-dd");
      });

      const byDayMap = {};
      readings.forEach((r) => {
        const ts = r.read_at || r.checked_at;
        if (!ts) return;
        const day = format(new Date(ts), "yyyy-MM-dd");
        byDayMap[day] = (byDayMap[day] || 0) + 1;
      });

      const dailyData = last15.map((day) => ({
        label: format(new Date(day + "T12:00:00"), "dd/MM", { locale: es }),
        count: byDayMap[day] || 0,
      }));

      const activeDays = Object.keys(byDayMap).length;
      const avgPerDay = activeDays > 0 ? (readings.length / activeDays).toFixed(1) : "0";

      // Horas trabajadas: primera → última medición por día (usando checked_at)
      const readingsByDay = {};
      readings.forEach((r) => {
        const ts = r.read_at || r.checked_at;
        if (!ts) return;
        const day = format(new Date(ts), "yyyy-MM-dd");
        if (!readingsByDay[day]) readingsByDay[day] = [];
        readingsByDay[day].push(new Date(ts).getTime());
      });
      let totalMs = 0;
      Object.values(readingsByDay).forEach((times) => {
        totalMs += Math.max(...times) - Math.min(...times);
      });
      const hoursWorked = (totalMs / (1000 * 60 * 60)).toFixed(1);

      const hourSlots = new Set();
      readings.forEach((r) => {
        const ts = r.read_at || r.checked_at;
        if (!ts) return;
        const d = new Date(ts);
        hourSlots.add(format(d, "yyyy-MM-dd-HH"));
      });
      const avgPerHour =
        hourSlots.size > 0 ? (readings.length / hourSlots.size).toFixed(1) : "0";

      setStats({
        total,
        totalPrev,
        dailyData,
        hoursWorked,
        avgPerDay,
        avgPerHour,
        activeDays,
        readings,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  }

  function handlePeriod(p) {
    setPeriod(p);
    if (selected) loadStats(selected, p);
  }

  const diff = stats ? stats.total - stats.totalPrev : 0;

  return (
    <div style={styles.root}>
      {/* Lista de Colaboradores */}
      <div style={styles.sidebar}>
        <h2 style={styles.sideTitle}>Usuarios</h2>
        {loading ? (
          <p style={styles.loading}>Cargando...</p>
        ) : (
          employees.map((emp) => (
            <button
              key={emp.id}
              style={{
                ...styles.empBtn,
                ...(selected?.id === emp.id ? styles.empBtnActive : {}),
              }}
              onClick={() => loadStats(emp)}
            >
              <div style={styles.empAvatar}>
                {emp.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={styles.empName}>{emp.name}</div>
                <div style={styles.empSub}>
                  {emp.active ? "Activo" : "Inactivo"}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Panel de stats */}
      <div style={styles.main}>
        {!selected ? (
          <div style={styles.empty}>
            <p style={styles.emptyIcon}>👈</p>
            <p style={styles.emptyText}>
              Seleccioná un Usuario para ver sus estadísticas
            </p>
          </div>
        ) : (
          <>
            <div style={styles.mainHeader}>
              <div>
                <h1 style={styles.mainTitle}>{selected.name}</h1>
                <p style={styles.mainSub}>
                  {selected.email} {selected.zone ? `· ${selected.zone}` : ""}
                </p>
              </div>
              <div style={styles.periodBtns}>
                <button
                  style={period === "month" ? styles.periodActive : styles.periodBtn}
                  onClick={() => handlePeriod("month")}
                >
                  Este mes
                </button>
                <button
                  style={period === "all" ? styles.periodActive : styles.periodBtn}
                  onClick={() => handlePeriod("all")}
                >
                  Todo el tiempo
                </button>
              </div>
            </div>

            {loadingStats ? (
              <div style={styles.loading}>Cargando estadísticas...</div>
            ) : (
              stats && (
                <>
                  {/* Cards */}
                  <div style={styles.cards}>
                    <StatCard
                      label={period === "month" ? "Medidores este mes" : "Total medidores"}
                      value={stats.total}
                      color="#D4620D"
                      sub={
                        period === "month" && diff !== 0
                          ? `${diff > 0 ? "+" : ""}${diff} vs mes anterior`
                          : null
                      }
                      subColor={diff >= 0 ? "#16a34a" : "#dc2626"}
                    />
                    <StatCard
                      label="Horas trabajadas (30 días)"
                      value={stats.hoursWorked}
                      color="#ea580c"
                      sub={`en ${stats.activeDays} día${stats.activeDays !== 1 ? "s" : ""} activo${stats.activeDays !== 1 ? "s" : ""}`}
                      subColor="#6b6b66"
                    />
                    <StatCard
                      label="Promedio por día activo (30 días)"
                      value={stats.avgPerDay}
                      color="#16a34a"
                      sub={`${stats.activeDays} días con medidores`}
                      subColor="#6b6b66"
                    />
                    <StatCard
                      label="Promedio por hora trabajada (30 días)"
                      value={stats.avgPerHour}
                      color="#9333ea"
                      sub="medidores / hora activa"
                      subColor="#6b6b66"
                    />
                  </div>

                  {/* Lecturas por día — últimos 30 días */}
                  <div style={styles.box}>
                    <h3 style={styles.boxTitle}>
                      Medidores por día — últimos 30 días
                    </h3>
                    <div style={styles.barChart}>
                      {(() => {
                        const max = Math.max(...stats.dailyData.map((d) => d.count), 1);
                        return stats.dailyData.map(({ label, count }) => (
                          <div key={label} style={styles.barCol}>
                            <div style={styles.barCount}>{count > 0 ? count : ""}</div>
                            <div style={styles.barWrap}>
                              <div
                                style={{
                                  ...styles.bar,
                                  height: `${(count / max) * 100}%`,
                                  background: count === 0 ? "transparent" : "#D4620D",
                                }}
                              />
                            </div>
                            <div style={styles.barLabel}>{label}</div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Lecturas recientes */}
                  <div style={styles.box}>
                    <h3 style={styles.boxTitle}>Medidores recientes (últimos 30 días)</h3>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {["Foto", "Código", "Fecha", "Ubicación", "Fachada"].map((h) => (
                            <th key={h} style={styles.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.readings.slice(0, 10).map((r, i) => {
                          const ts = r.read_at || r.created_at;
                          return (
                          <tr key={r.id || i}>
                            <td style={styles.td}>
                              {r.photo ? <img src={r.photo} alt="" style={styles.thumb} loading="lazy" /> : "—"}
                            </td>
                            <td style={styles.td}>{r.meter_code || "—"}</td>
                            <td style={styles.td}>
                              {ts ? format(new Date(ts), "dd/MM/yy HH:mm", { locale: es }) : "—"}
                            </td>
                            <td style={styles.td}>
                              {r.latitude && r.longitude ? (
                                <span style={styles.coords}>
                                  {parseFloat(r.latitude).toFixed(4)},{" "}
                                  {parseFloat(r.longitude).toFixed(4)}
                                </span>
                              ) : "—"}
                            </td>
                            <td style={styles.td}>—</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, sub, subColor }) {
  return (
    <div style={{ ...styles.card, borderTop: `4px solid ${color}` }}>
      <div style={{ ...styles.cardValue, color }}>{value}</div>
      <div style={styles.cardLabel}>{label}</div>
      {sub && (
        <div style={{ fontSize: 13, color: subColor, marginTop: 4, fontWeight: 700 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { display: "flex", gap: 24, height: "calc(100vh - 64px)" },
  sidebar: {
    width: 220,
    flexShrink: 0,

borderRadius: 14,
    padding: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflowY: "auto",
  },
  sideTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 12,
    marginTop: 0,
  },
  empBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    marginBottom: 4,
    textAlign: "left",
  },
  empBtnActive: { background: "var(--primary-bg)" },
  empAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#D4620D",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,fontSize: 15,
    flexShrink: 0,
  },
  empName: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  empSub: { fontSize: 13, color: "var(--text-2)" },
  main: { flex: 1, overflowY: "auto" },
  mainHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  mainTitle: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },
  mainSub: { fontSize: 13, color: "var(--text-2)", margin: "4px 0 0" },
  periodBtns: { display: "flex", gap: 8 },
  periodBtn: {
    padding: "7px 14px",
    borderRadius: 10,

cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
  },
  periodActive: {
    padding: "7px 14px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    cursor: "pointer",fontSize: 13,
    fontWeight: 700,
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    marginBottom: 20,
  },
  card: {
    borderRadius: 14,
    padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  cardValue: { fontSize: 32, fontWeight: 700, marginBottom: 4 },
  cardLabel: { fontSize: 13, color: "var(--text-2)", fontWeight: 700 },
  box: {
    borderRadius: 14,
    padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    marginBottom: 16,
  },
  boxTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 14,
    marginTop: 0,
  },
  barChart: {
    display: "flex",
    gap: 8,
    alignItems: "flex-end",
    height: 120,
    overflowX: "auto",
  },
  barCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 36,
    flex: 1,
  },
  barCount: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 4,
    minHeight: 15,
  },
  barWrap: { width: 24, height: 80, display: "flex", alignItems: "flex-end" },
  bar: {
    width: "100%",
    background: "#D4620D",
    borderRadius: "4px 4px 0 0",
    minHeight: 4,
  },
  barLabel: {
    fontSize: 13,
    color: "var(--text-2)",
    marginTop: 4,
    whiteSpace: "nowrap",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "8px 12px",
    textAlign: "left",fontSize: 13,
    color: "var(--text-2)",
    fontWeight: 700,
    borderBottom: "1px solid var(--border)",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid var(--border)",
    verticalAlign: "middle",fontSize: 13,
    color: "var(--text)",
  },
  thumb: { width: 44, height: 44, borderRadius: 10, objectFit: "cover" },
  coords: { fontSize: 13, color: "var(--text-2)", fontFamily: "Montserrat, sans-serif" },
  loading: { padding: 40, textAlign: "center", color: "var(--text-2)" },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60%",
  },
  emptyIcon: { fontSize: 48, marginBottom: 12 },
  emptyText: { fontSize: 16, color: "var(--text-2)" },
};
